import { ConflictError } from "@/api/ConflictError";
import { usePasteMembersMutation } from "@/api/memberQueryOptions";
import { MemberType, NewMemberType } from "@/api/members";
import { MembershipType } from "@/api/memberships";
import { NoPermissionError } from "@/api/NoPermissionError";
import { ValidationError } from "@/api/ValidationError";
import { ApiErrorComponent } from "@/components/ApiErrorComponent";
import { useColumnState } from "@/components/hooks/useColumnState";
import UploadMemberToolbar from "@/ui/member/UploadMemberToolbar";
import { Alert, Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { DataGridPremium, GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { t } from "i18next";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

export const Route = createFileRoute('/_auth/uploadMember')({
    component: UploadMemberComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
});

interface UploadMemberType extends MemberType {
    scoutstate?: string;
    section?: string;
}

function UploadMemberComponent() {

    const [backdropOpen, setBackdropOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [data, setData] = useState<UploadMemberType[]>([]);

    const scoutstates = [
        { value: 'MEMBER_FULL', label: t('enum.scoutstate.MEMBER_FULL') },
        { value: 'MEMBER_ADD', label: t('enum.scoutstate.MEMBER_ADD') },
        { value: 'CONTACT', label: t('enum.scoutstate.CONTACT') },
    ]

    const columns =
        [
            { field: 'firstname', headerName: t('property.member.firstname'), minWidth: 100, editable: true },
            { field: 'lastname', headerName: t('property.member.lastname'), minWidth: 100, editable: true },
            {
                field: 'birthdate', headerName: t('property.member.birthdate'), minWidth: 100, editable: true,
                valueFormatter: (value) => {
                    if (typeof value === 'string') {
                        return dayjs(value, 'DD.MM.YYYY').format('DD.MM.YYYY');
                    } else if (typeof value === 'object' && value as any instanceof Date) {
                        return dayjs(value as Date).format('DD.MM.YYYY');
                    }
                }
            },
            { field: 'street', headerName: t('property.member.street'), minWidth: 100, editable: true },
            { field: 'postcode', headerName: t('property.member.postcode'), minWidth: 100, editable: true },
            { field: 'country', headerName: t('property.member.country'), minWidth: 100, editable: true },
            { field: 'city', headerName: t('property.member.city'), minWidth: 100, editable: true },
            { field: 'country', headerName: t('property.member.country'), minWidth: 100, editable: true },
            { field: 'emailPrimary', headerName: t('property.member.emailPrimary'), minWidth: 100, editable: true },
            { field: 'emailSecondary', headerName: t('property.member.emailSecondary'), minWidth: 100, editable: true },
            { field: 'phoneLine', headerName: t('property.member.phoneLine'), minWidth: 100, editable: true },
            { field: 'phoneMobile', headerName: t('property.member.phoneMobile'), minWidth: 100, editable: true },
            {
                field: 'scoutstate',
                headerName: t('property.membership.scoutstate'),
                minWidth: 100,
                editable: true,
                type: 'singleSelect',
                valueOptions: scoutstates,
            },
            {
                field: 'section',
                headerName: t('entity.section'),
                minWidth: 100,
                editable: true,
            }

        ] as GridColDef[]

    const apiRef = useGridApiRef();
    const columnsState = useColumnState('pasteMember', apiRef, columns)

    const navigate = useNavigate();
    const onSuccess = (newMemberships: MembershipType[]) => {
        setBackdropOpen(false)
        enqueueSnackbar(t('message.membersCreated', { count: newMemberships.length }), { variant: 'success' });
        navigate({
            to: "/membershipsOverview", search: {
                filter: [
                    { field: "active", operator: "is", value: "true" },
                    { field: "scoutstate", operator: "isAnyOf", value: ['MEMBER_FULL', 'MEMBER_ADD'] }
                ],
                sort: [{ field: "member.scoutId", direction: "asc" }],
                visibility: [{ field: "properties.joblist" }, { field: "properties.section" }],
            }
        })
    }
    const onError = (error: Error) => {
        setBackdropOpen(false)
        if (error instanceof ConflictError) {
            enqueueSnackbar(t('error.message', { message: t('error.code.' + error.code) }), { variant: 'error' });
        } else if (error instanceof ValidationError) {
            enqueueSnackbar(t('error.message', { message: error.message }), { variant: 'error' });
        } else if (error instanceof NoPermissionError) {
            enqueueSnackbar(t('error.noPermission'), { variant: 'error' });
        } else {
            enqueueSnackbar(t('error.default'), { variant: 'error' });
        }
    }

    const pasteMembers = usePasteMembersMutation(onSuccess, onError);

    const handleCancel = () => {
        setDialogOpen(false);
    }

    const handlePersist = () => {
        setDialogOpen(false);
        persistData();
    }

    const startPersist = () => {
        setDialogOpen(true);
    }

    const persistData = () => {
        setBackdropOpen(true)
        try {
            const members: NewMemberType[] = data.map((row) => {
                return {
                    ...row,
                    birthdate: row.birthdate && row.birthdate instanceof Date ? row.birthdate : dayjs(row.birthdate, 'DD.MM.YYYY').toDate(),
                    section: { abbreviation: row.section },
                    scoutstate: row.scoutstate || 'MEMBER_FULL',
                } as NewMemberType
            });
            pasteMembers.mutate(members)
        } catch (error) {
            setBackdropOpen(false)
            enqueueSnackbar(t('error.message', { message: error }), { variant: 'error' });
            return;
        }

    }

    const handleRawData = (raw_data: string[][]) => {
        const header = raw_data[0] as string[];
        const data = raw_data.slice(1).map((row) => {
            const firstnameIndex = header.indexOf(t('property.member.firstname'));
            const lastnameIndex = header.indexOf(t('property.member.lastname'));
            const birthdateIndex = header.indexOf(t('property.member.birthdate'));
            const streetIndex = header.indexOf(t('property.member.street'));
            const postcodeIndex = header.indexOf(t('property.member.postcode'));
            const cityIndex = header.indexOf(t('property.member.city'));
            const countryIndex = header.indexOf(t('property.member.country'));
            const emailPrimaryIndex = header.indexOf(t('property.member.emailPrimary'));
            const emailSecondaryIndex = header.indexOf(t('property.member.emailSecondary'));
            const phoneLineIndex = header.indexOf(t('property.member.phoneLine'));
            const phoneMobileIndex = header.indexOf(t('property.member.phoneMobile'));
            const sectionIndex = header.indexOf(t('entity.section'));
            const scoutStateIndex = header.indexOf(t('property.membership.scoutstate'));

            const firstname = firstnameIndex !== -1 ? row[firstnameIndex] : '';
            const lastname = lastnameIndex !== -1 ? row[lastnameIndex] : '';
            const birthdateStr = lastnameIndex !== -1 ? row[birthdateIndex] : '';
            const birthdate = birthdateStr ? dayjs(birthdateStr.trim()).toDate() : undefined;
            const street = streetIndex !== -1 ? row[streetIndex] : '';
            const postcode = postcodeIndex !== -1 ? row[postcodeIndex] : '';
            const city = cityIndex !== -1 ? row[cityIndex] : '';
            const country = countryIndex !== -1 ? row[countryIndex] : '';
            const emailPrimary = emailPrimaryIndex !== -1 ? row[emailPrimaryIndex] : '';
            const phoneMobile = phoneMobileIndex !== -1 ? row[phoneMobileIndex] : '';
            const emailSecondary = emailSecondaryIndex !== -1 ? row[emailSecondaryIndex] : '';
            const phoneLine = phoneLineIndex !== -1 ? row[phoneLineIndex] : '';
            const scoutstate = scoutStateIndex !== -1 ? row[scoutStateIndex] : '';
            const section = sectionIndex !== -1 ? row[sectionIndex] : '';

            return {
                uuid: crypto.randomUUID(),
                firstname: firstname.trim(),
                lastname: lastname.trim(),
                birthdate: birthdate,
                street: street.trim(),
                postcode: postcode.trim(),
                city: city.trim(),
                country: country.trim(),
                emailPrimary: emailPrimary.trim(),
                emailSecondary: emailSecondary.trim(),
                phoneLine: phoneLine.trim(),
                phoneMobile: phoneMobile.trim(),
                scoutstate: scoutstate.trim(),
                section: section.trim(),
            } as UploadMemberType;

        })
        setData(data.filter((row) => row.firstname || row.lastname));
    }

    const CustomToolbar = () => {
        return <UploadMemberToolbar persistData={startPersist} handleRawData={handleRawData} />
    }

    return (<>
        <Alert severity="warning">{t('message.uploadMembers')}</Alert>
        <DataGridPremium
            apiRef={apiRef}
            columns={columnsState.columns}
            onColumnWidthChange={columnsState.onColumnWidthChange}
            onColumnOrderChange={columnsState.onColumnOrderChange}
            rows={data}
            editMode="cell"
            getRowId={(row) => row.uuid}
            hideFooter={true}
            ignoreValueFormatterDuringExport={true}
            density='compact'
            disableAggregation
            disableColumnPinning
            disablePivoting
            disableColumnSorting
            disableRowGrouping
            disableColumnFilter
            slots={{
                toolbar: CustomToolbar
            }}
            showToolbar={true}
        />
        <Backdrop
            sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
            open={backdropOpen}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
        <Dialog
            open={dialogOpen}
            onClose={handleCancel}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {"Mitglieder anlegen?"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {t('message.uploadMembers')}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel}>Abbrechen</Button>
                <Button onClick={handlePersist} autoFocus variant="outlined" color="error">
                    Anlegen
                </Button>
            </DialogActions>
        </Dialog>
    </>)

}